import React from 'react';
import { Button } from 'react-bootstrap';
import { useTranslate } from '@tolgee/react';

import EmptyImagePlaceholder from '@/assets/images/placeholders/images/placeholder-image-square.jpg';
import { PLATFORM_STATIC_ICONS_PNG } from '@/config/icons';

import styles from './LomavisCampaignLivePreview1.module.css';

const getPlatformFromPlacementKey = (
  key: string
): 'facebook' | 'instagram' | null => {
  if (key.startsWith('instagram')) return 'instagram';
  if (key.startsWith('facebook')) return 'facebook';
  return null;
};

const PlacementIcon: React.FC<{ placementKey: string }> = ({
  placementKey
}) => {
  const platform = getPlatformFromPlacementKey(placementKey);
  if (!platform) return null;
  return (
    <img
      src={PLATFORM_STATIC_ICONS_PNG[platform]}
      alt=""
      style={{ height: 16, width: 16, objectFit: 'contain' }}
    />
  );
};

export type LomavisCampaignLivePreviewPlacement1 = {
  key: string;
  label: string;
};

export type LomavisCampaignLivePreviewAd1 = {
  accountName: string;
  profileImageUrl?: string;
  sponsoredLabel: string;
  primaryText: string;
  landingUrl: string;
  cta: string;
};

export type LomavisCampaignLivePreviewMedia1 = {
  name: string;
  kind?: 'image' | 'video' | 'file';
  previewUrl?: string;
};

export interface LomavisCampaignLivePreview1Props {
  placements: LomavisCampaignLivePreviewPlacement1[];
  activePlacementKey: string;
  onPlacementChange?: (key: string) => void;
  ad: LomavisCampaignLivePreviewAd1;
  media?: LomavisCampaignLivePreviewMedia1;
  mediaFiles?: LomavisCampaignLivePreviewMedia1[];
  onRefresh?: () => void;
}

const renderMediaContent = (
  media?: LomavisCampaignLivePreviewMedia1,
  emptyAlt = ''
) => {
  if (media?.previewUrl && media.kind === 'image') {
    return (
      <img
        className={`${styles.mediaPreview} w-100 rounded-3`}
        src={media.previewUrl}
        alt={media.name}
      />
    );
  }

  if (media?.previewUrl && media.kind === 'video') {
    return (
      <video
        className={`${styles.mediaPreview} w-100 rounded-3`}
        src={media.previewUrl}
        aria-label={media.name}
        muted
        playsInline
        controls
      />
    );
  }

  if (media) {
    return (
      <div
        className={`${styles.filePreview} d-flex flex-column align-items-center justify-content-center gap-2 rounded-3 bg-white text-center small fw-bold text-body-secondary`}
      >
        <i className="fa-regular fa-file-image" aria-hidden="true" />
        <span>{media.name}</span>
      </div>
    );
  }

  return (
    <img
      className={`${styles.emptyMediaPreview} w-100 rounded-3`}
      src={EmptyImagePlaceholder}
      alt={emptyAlt}
    />
  );
};

export const LomavisCampaignLivePreview1: React.FC<
  LomavisCampaignLivePreview1Props
> = ({
  placements,
  activePlacementKey,
  ad,
  media,
  mediaFiles,
  onRefresh
}) => {
  const { t } = useTranslate();
  const activePlacement =
    placements.find((placement) => placement.key === activePlacementKey) ??
    placements[0];
  const avatarInitial = (ad.accountName || 'L').trim().charAt(0).toUpperCase();
  const previewMediaFiles = mediaFiles?.length ? mediaFiles : media ? [media] : [];
  const previewMedia = previewMediaFiles[0];

  return (
    <div className="vstack gap-3 rounded-3 border bg-white p-3">
      <div className="d-flex align-items-center justify-content-between gap-3">
        <div className="fw-bold text-body">
          {t('adManager.preview.livePreview', 'Live preview')}
        </div>
        <div className="d-flex align-items-center gap-2">
          {activePlacement && (
            <span className="d-inline-flex align-items-center gap-2 small fw-semibold text-body-secondary">
              <PlacementIcon placementKey={activePlacement.key} />
              <span>{activePlacement.label}</span>
            </span>
          )}
          {onRefresh && (
            <Button
              type="button"
              variant="outline-secondary"
              size="sm"
              onClick={onRefresh}
            >
              {t('common.refresh', 'Refresh')}
            </Button>
          )}
        </div>
      </div>
      <div className="overflow-hidden rounded-3 border bg-white">
        <div className="p-3">
          <div className="d-flex align-items-center gap-2">
            {ad.profileImageUrl ? (
              <img
                className={`${styles.avatar} rounded-circle`}
                src={ad.profileImageUrl}
                alt=""
              />
            ) : (
              <span
                className={`${styles.avatar} d-inline-flex align-items-center justify-content-center rounded-circle bg-primary fw-bold text-white`}
              >
                {avatarInitial}
              </span>
            )}
            <div>
              <div className="fw-bold text-body">{ad.accountName}</div>
              <div className="text-muted small fw-semibold">
                {ad.sponsoredLabel}
              </div>
            </div>
          </div>
        </div>
        <div className="px-3 pb-3">
          <p className="m-0 text-body small lh-base">{ad.primaryText}</p>
        </div>
        <div
          className={`${styles.visual} d-grid align-items-end gap-2 p-4`}
          aria-label={t(
            'adManager.preview.mockCampaignCreative',
            'Mock campaign creative'
          )}
        >
          {renderMediaContent(previewMedia)}
        </div>
        <div className="d-flex align-items-center justify-content-between gap-3 bg-light p-3">
          <div style={{ minWidth: 0 }}>
            <div className="text-muted text-uppercase small fw-bold">
              {ad.landingUrl}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm flex-shrink-0 fw-bold"
          >
            {ad.cta}
          </button>
        </div>
        <div className="d-flex align-items-center justify-content-between gap-3 border-top px-3 py-2 text-muted small fw-bold">
          <span>{t('adManager.preview.like', 'Like')}</span>
          <span>{t('adManager.preview.comment', 'Comment')}</span>
          <span>{t('adManager.preview.share', 'Share')}</span>
        </div>
      </div>
    </div>
  );
};
